import { useMemo, useState, type ReactNode } from "react";
import { useDndContext } from "@dnd-kit/core";
import {
    useSortable,
    type AnimateLayoutChanges,
    type NewIndexGetter,
    type UseSortableArguments,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import classNames from "classnames";
import { mergeRefs } from "@design-system/overlay/mergeRefs";
import type { BoardCardContext, BoardLock } from "./Board.types";
import { placementKeyProps } from "./boardAttributes";
import type { BoardCellEntry } from "./boardCells";
import type { BoardEntrySlot } from "./BoardCellList";
import { BoardCardDragContext } from "./boardCardDrag";
import { BoardCardItemIdContext } from "./boardCardExpansion";
import { useBoardCardEntrance, useBoardCardLanding } from "./boardEntrance";
import { BoardCountBadge } from "./BoardGroupOverlay";
import { BoardCardKeyContext } from "./boardRoving";
import { useIsBoardItemSelected } from "./useBoardSelection";
import styles from "./Board.module.scss";
import dragStyles from "./BoardDrag.module.scss";

export type BoardSlotLook = "card" | "slot" | "lifted" | "carried";

interface BoardSortableCardProps<TItem> {
    entry: BoardCellEntry<TItem>;
    slot: BoardEntrySlot;
    /** Another copy of the dragged item: outlined while the drag lasts. */
    isSiblingOfDragged: boolean;
    renderCard: (item: TItem, context: BoardCardContext) => ReactNode;
    /**
     * `slot` while the overlay carries the card, `lifted` for keyboard drags,
     * `carried` (dimmed) for the rest of a dragged selection.
     */
    look: BoardSlotLook;
    /**
     * A pointer drag is on, not yet dropped: a slot that mounts now holds a
     * spacer, and its card mounts once the card drops.
     */
    isPointerDrag: boolean;
    /** Its place among the cards a group drop just moved, which enter in turn. */
    landingIndex?: number;
    /** Lifted from the keyboard with a selection: every card it carries. */
    carriedCount?: number;
    transition: UseSortableArguments["transition"];
    registerActivator: (placementKey: string, node: HTMLElement | null) => void;
    lock: BoardLock | null;
    onLockedPickUp: (item: TItem, lock: BoardLock) => void;
}

// The board moves cards between slots itself, so neighbours shift by layout
// animation (FLIP) instead of dnd-kit's displacement strategy; the dragged
// slot itself never animates because the overlay or focus already marks it.
const animateLayoutChanges: AnimateLayoutChanges = ({
    isSorting,
    isDragging,
    transition,
}) => isSorting && !isDragging && transition !== null;
const getNewIndex: NewIndexGetter = ({ id, items }) => items.indexOf(id);

const SORTABLE_ATTRIBUTES = { roleDescription: "draggable card" };
const LOCKED_ATTRIBUTES = {};

// The slot look hides its card, so a slot that mounts mid-drag (the card
// previewed in another column) holds the dragged card's height instead of
// mounting a hidden copy of it in every column the pointer crosses.
const BoardSlotSpacer = () => {
    const { activeNodeRect } = useDndContext();
    return <div style={{ height: activeNodeRect?.height }} />;
};

/** A card's `li` on a draggable board; the card itself is the drag handle. */
export const BoardSortableCard = <TItem,>({
    entry,
    slot,
    isSiblingOfDragged,
    renderCard,
    look,
    isPointerDrag,
    landingIndex,
    carriedCount,
    transition,
    registerActivator,
    lock,
    onLockedPickUp,
}: Readonly<BoardSortableCardProps<TItem>>) => {
    const { item, placement, placementKey } = entry;
    const isSelected = useIsBoardItemSelected(entry.itemId);
    const isDraggedSlot = isPointerDrag && look === "slot";
    const [isCardDeferred, setIsCardDeferred] = useState(isDraggedSlot);
    if (isCardDeferred && !isDraggedSlot) {
        setIsCardDeferred(false);
    }
    const {
        attributes,
        listeners,
        setNodeRef,
        setActivatorNodeRef,
        isDragging,
        transform,
        transition: transitionValue,
    } = useSortable({
        id: placementKey,
        transition,
        animateLayoutChanges,
        getNewIndex,
        attributes: SORTABLE_ATTRIBUTES,
        disabled: { draggable: lock !== null, droppable: false },
    });
    const activatorRef = useMemo(
        () =>
            mergeRefs<HTMLElement>([
                setActivatorNodeRef,
                (node) => registerActivator(placementKey, node),
            ]),
        [setActivatorNodeRef, registerActivator, placementKey],
    );
    const { measureRef } = slot;
    const entranceRef = useBoardCardEntrance(entry.itemId, slot);
    const landingRef = useBoardCardLanding(landingIndex);
    const nodeRef = useMemo(
        () =>
            mergeRefs<HTMLElement>([
                setNodeRef,
                measureRef,
                entranceRef,
                landingRef,
            ]),
        [setNodeRef, measureRef, entranceRef, landingRef],
    );
    const isLifted = look === "lifted";
    const dragHandle = useMemo(
        () => ({
            activatorRef,
            attributes: lock === null ? attributes : LOCKED_ATTRIBUTES,
            listeners,
            isDragging,
            isLifted,
            lock,
            onLockedPickUp: () => {
                if (lock !== null) {
                    onLockedPickUp(item, lock);
                }
            },
        }),
        [
            activatorRef,
            attributes,
            listeners,
            isDragging,
            isLifted,
            lock,
            onLockedPickUp,
            item,
        ],
    );

    return (
        <li
            ref={nodeRef}
            data-index={slot.index}
            {...placementKeyProps(placementKey)}
            className={classNames(styles.cardSlot, dragStyles[look])}
            data-sibling-of-dragged={isSiblingOfDragged}
            style={{
                transform: CSS.Translate.toString(transform),
                transition: transitionValue,
            }}
        >
            {slot.gapAdds}
            {isCardDeferred && isDraggedSlot ? (
                <BoardSlotSpacer />
            ) : (
                <BoardCardItemIdContext.Provider value={entry.itemId}>
                    <BoardCardKeyContext.Provider value={placementKey}>
                        <BoardCardDragContext.Provider value={dragHandle}>
                            {renderCard(item, {
                                placement,
                                placementKey,
                                isSelected,
                            })}
                        </BoardCardDragContext.Provider>
                    </BoardCardKeyContext.Provider>
                </BoardCardItemIdContext.Provider>
            )}
            {carriedCount !== undefined && (
                <BoardCountBadge count={carriedCount} />
            )}
        </li>
    );
};
